import { Button } from "primereact/button";
import { Steps } from "primereact/steps";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PersonalData from "./PersonalData";
import StudentData from "./StudentData";
import GradeData from "./GradeData";
import PreviousEducation from "./PreviousEducation";
import { Card } from "primereact/card";
import { Avatar } from "primereact/avatar";
import { FileUpload, FileUploadHandlerEvent } from "primereact/fileupload";
import { studentsApi } from "../../services/students/studentsApi";
import { Message } from "primereact/message";
import { Skeleton } from "primereact/skeleton";
import Error from "../../components/common/Error";
import { FormikProvider, useFormik } from "formik";
import { Toast } from "primereact/toast";
import { CopyToClipboard } from "react-copy-to-clipboard";
import ActionButtons from "./ActionButtons";
import PaymentsInformation from "./PaymentsInformation";
import AdditionalInformation from "./AdditionalInformation";
import validationSchema from "./helpers/validationSchema";
import { StudentInfoAdminRequest } from "../../models/dtos";
import StudentDocuments from "./StudentDocuments";
import { Tag } from "primereact/tag";
import { format } from "date-fns";

const StudentProfile = () => {
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const { studentId } = useParams();
  const location = useLocation();
  const enrollmentWarnings = (location.state as { enrollmentWarnings?: string[] } | null)?.enrollmentWarnings ?? [];

  const { data: student, isLoading, error } = studentsApi.useGetStudentQuery(parseInt(studentId!), {
    refetchOnMountOrArgChange: true,
  });

  const [
    updateStudentProfile,
    { isLoading: isUpdateStudentLoading, isError: isUpdateStudentError },
  ] = studentsApi.useUpdateStudentMutation();

  const [createStudent, { isLoading: isCreateStudentLoading, isError: isCreateStudentError }] =
    studentsApi.useCreateStudentMutation();

  const [updateStudentPicture, { isLoading: isUpdatingStudentPicture }] = studentsApi.useUploadStudentPictureMutation();

  const [passportFile, setPassportFile] = useState<File | null>(null);

  const steps = [
    { label: "Лични данни" },
    { label: "Студентски данни" },
    ...(student?.data?.studentId
      ? [
          { label: "Допълнителни данни" },
          { label: "Оценки" },
          { label: "Предходно образование" },
          { label: "Записвания" },
          { label: "Документи" },
        ]
      : []),
  ];

  useEffect(() => {
    const stepIndex = parseInt(window.location.hash.replace("#", ""));
    if (!Number.isNaN(stepIndex)) {
      console.log(stepIndex);
      setActiveStep(stepIndex);
    }
  }, []);

  const handleSelectStep = (e: any) => {
    window.location.hash = e.index;
    setActiveStep(e.index);
  };

  const studentNav = (
    <div className="mt-4 flex flex-row justify-content-between">
      <div className="flex-grow-1">
        <Steps
          model={steps}
          activeIndex={activeStep}
          onSelect={handleSelectStep}
          readOnly={false}
          pt={{
            label: {
              className: "font-bold hover:text-primary",
            },
          }}
        />
      </div>

      <div className="flex flex-column align-items-center pl-4" style={{ flexBasis: "17%" }}>
        <Button
          label="Запази"
          className="w-full text-xl font-bold py-3"
          size="large"
          icon="pi pi-save"
          badge="Ctrl + S"
          onClick={() => formik.handleSubmit()}
          disabled={isUpdateStudentLoading || isCreateStudentLoading}
        />
      </div>
    </div>
  );

  const onUpload = (event: FileUploadHandlerEvent) => {
    updateStudentPicture({ studentId: student?.data.studentId!, picture: event.files[0] }).then((response) => {
      if ("data" in response && response.data) {
        toast.current!.show({ severity: "success", summary: "Успешно", detail: "Снимката е качена." });
      } else {
        toast.current!.show({
          severity: "error",
          summary: "Грешка",
          detail: "Снимката не е качена, моля проверете файла.",
        });
      }
    });
  };

  const formik = useFormik({
    initialValues: {
      isPreparatoryYear: false,
      ...student?.data,
      birthDate: student?.data.birthDate ? new Date(student?.data.birthDate) : null,
      passportIssuedOn: student?.data.passportIssuedOn ? new Date(student?.data.passportIssuedOn) : null,
      relocationDate: student?.data.relocationDate ? new Date(student?.data.relocationDate) : null,
    } as Partial<StudentInfoAdminRequest>,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (!student?.data.studentId && values.isPreparatoryYear && (!values.facultyId || !values.degreeId || !values.specialityId)) {
        toast.current!.show({
          severity: "error",
          summary: "Грешка",
          detail: "Изберете факултет и ОКС със специалност Подготвителна година.",
        });
        return;
      }
      if (!student?.data.studentId) {
        console.log("Creating student with passport file:", passportFile);
        createStudent({ studentData: values, passport: passportFile }).then((response) => {
          if (
            "data" in response &&
            response.data &&
            "data" in response.data &&
            typeof response.data.data === "object" &&
            response.data.data.studentId
          ) {
            toast.current!.show({ severity: "success", summary: "Успешно", detail: "Студентът е регистриран." });
            navigate(`/students/${response.data.data.studentId}`);
          } else {
            toast.current!.show({
              severity: "error",
              summary: "Грешка",
              detail: response.data?.message || "Възникна грешка при регистрирането на студента.",
            });
          }
        });
      } else {
        updateStudentProfile(values).then((response) => {
          if ("data" in response && response.data?.data === true) {
            toast.current!.show({
              severity: "success",
              summary: "Успешно",
              detail: "Данните на студента са запазени.",
            });
          } else {
            toast.current!.show({
              severity: "error",
              summary: "Грешка",
              detail: "Възникна грешка при запазването на данните.",
            });
          }
        });
      }
    },
  });

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === "s") {
        event.preventDefault();
        formik.handleSubmit();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [formik]);

  if (isLoading) return <Skeleton className="w-full h-2rem" />;
  if (error) return <Error error={error} />;

  return (
    <div>
      <FormikProvider value={formik}>
        {!student?.data?.studentId && <h1 className="font-light text-center">Регистриране на нов студент</h1>}

        {studentNav}

        {enrollmentWarnings.length > 0 && (
          <Message
            severity="warn"
            className="w-full my-4"
            content={
              <div className="flex flex-column gap-1">
                <strong>Студентът е записан с предупреждения:</strong>
                {enrollmentWarnings.map((warning, index) => (
                  <span key={index}>{warning}</span>
                ))}
              </div>
            }
          />
        )}

        {(isUpdateStudentError || isCreateStudentError) && (
          <Message severity="error" text="Възникна грешка при запазването на данните." className="w-full my-4" />
        )}

        {formik.errors && Object.keys(formik.errors).length > 0 && (
          <Message
            severity="error"
            text="Моля попълнете всички задължителни полета в 1. Лични данни и 2. Студентски данни."
            className="w-full my-4"
          />
        )}

        <div>
          {student?.data?.studentId && (
            <Card className="w-full mt-4">
              <div className="flex flex-row">
                {isUpdatingStudentPicture && <Skeleton className="w-8rem h-12rem mr-4" />}
                {!isUpdatingStudentPicture && (
                  <div className="text-center">
                    <Avatar
                      image={`data:image/jpeg;base64,${student?.data.picture}`}
                      style={{ width: "160px", height: "200px" }}
                      shape="square"
                    ></Avatar>
                    <FileUpload
                      mode="basic"
                      name="picture"
                      accept="image/*"
                      maxFileSize={5000000}
                      customUpload
                      auto
                      uploadHandler={onUpload}
                      chooseLabel="Снимка"
                      chooseOptions={{
                        className: "p-button-outlined p-button-secondary",
                      }}
                    />
                  </div>
                )}

                <div className="flex-grow-1 text-left pl-4">
                  <h2 className="text-4xl font-normal mt-4">
                    <div className="text-sm">Факултетен номер:</div>
                    <div>
                      {student?.data.facultyNumber}
                      <CopyToClipboard
                        text={student?.data.facultyNumber!}
                        onCopy={() =>
                          toast.current!.show({
                            severity: "info",
                            summary: "Копирано",
                            detail: "Факултетният номер е копиран в клипборда.",
                          })
                        }
                      >
                        <Button
                          icon="pi pi-copy"
                          size="large"
                          className="p-button-rounded p-button-secondary p-button-text"
                          tooltip="Копиране на факултетен номер в клипборда."
                          tooltipOptions={{ position: "bottom" }}
                        />
                      </CopyToClipboard>
                    </div>
                  </h2>

                  <h2 className="text-3xl font-bold">
                    <div>
                      {student?.data.firstName} {student?.data.middleName} {student?.data.lastName}
                    </div>
                    <div className="font-normal">
                      {student?.data.firstNameEn} {student?.data.middleNameEn} {student?.data.lastNameEn}
                    </div>
                    <div className="font-normal flex flex-row gap-2 align-items-center">
                      <span className="pi pi-calendar" title="ЕГН/ЛНЧ/ИДН"></span> {student?.data.egn}
                      <CopyToClipboard
                        text={student?.data.egn!}
                        onCopy={() =>
                          toast.current!.show({
                            severity: "info",
                            summary: "Копирано",
                            detail: "ЕГН е копиран в клипборда.",
                          })
                        }
                      >
                        <Button
                          icon="pi pi-copy"
                          className="p-button-rounded p-button-secondary p-button-text"
                          tooltip="Копиране на ЕГН/ЛНЧ/ИДН в клипборда."
                          tooltipOptions={{ position: "bottom" }}
                        />
                      </CopyToClipboard>
                      <Button
                        icon="pi pi-search"
                        className="p-button-rounded p-button-secondary p-button-text"
                        tooltip="Търсене в Студенти по ЕГН/ЛНЧ/ИДН"
                        tooltipOptions={{ position: "bottom" }}
                      />
                    </div>
                    {student?.data.userEmail && (
                      <div className="font-normal flex flex-row gap-2 align-items-center">
                        <span className="pi pi-envelope" title="Студентски и-мейл"></span>
                        {student?.data.userEmail}
                        <CopyToClipboard
                          text={student?.data.userEmail!}
                          onCopy={() =>
                            toast.current!.show({
                              severity: "info",
                              summary: "Копирано",
                              detail: "Студентският и-мейл е копиран в клипборда.",
                            })
                          }
                        >
                          <Button
                            icon="pi pi-copy"
                            className="p-button-rounded p-button-secondary p-button-text"
                            tooltip="Копиране на студентски и-мейл в клипборда."
                            tooltipOptions={{ position: "bottom" }}
                          />
                        </CopyToClipboard>
                      </div>
                    )}
                  </h2>
                </div>

                <div className="flex-auto text-left pl-4 pt-4">
                  <h3>
                    <div className="text-sm">Факултет:</div> {student?.data.facultyName}
                  </h3>
                  <h3>
                    <div className="text-sm">Специалност:</div> {student?.data.specialityName}
                  </h3>
                  <h3>
                    <div className="text-sm">ЕАН:</div> {student?.data.uan ?? "Няма"}
                    {student?.data.uan && (
                      <>
                        <a
                          href={`https://regstudents.nacid.bg/personLot/${student?.data.uan}/Student`}
                          target="_blank"
                          className="text-blue-500 hover:text-blue-700 font-normal ml-2"
                        >
                          <i className="pi pi-external-link mr-2"></i>
                          Регистър
                        </a>
                        <a
                          href={`https://nesc.nacid.bg/search/student?uan=${student?.data.uan}`}
                          target="_blank"
                          className="text-blue-500 hover:text-blue-700 font-normal ml-2"
                        >
                          <i className="pi pi-credit-card mr-2"></i>
                          НЕСК
                        </a>
                      </>
                    )}
                  </h3>
                  {student?.data.und && (
                    <h3>
                      <div className="text-sm">ЕНД:</div> {student?.data.und}
                    </h3>
                  )}
                  <div className="flex flex-row gap-4 align-items-start">
                    <h3>
                      <div className="text-sm">Статус:</div>{" "}
                      <Tag
                        value={student?.data.studentStatusName}
                        severity={student?.data.studentStatusName === "приключил" ? "danger" : "info"}
                      ></Tag>
                    </h3>
                    <h3>
                      <div className="text-sm">Семестър:</div> {student?.data.semester}
                    </h3>
                    <h3>
                      <div className="text-sm">Заверен семестър:</div> {student?.data.lastSemesterAttestation?.semester}
                      {" / "}
                      {student?.data.lastSemesterAttestation?.semesterAttestationTypeName}
                      {" / "}
                      {student?.data.lastSemesterAttestation?.attestationDate && (
                        <>{format(student?.data.lastSemesterAttestation?.attestationDate, "dd.MM.yyyy")}</>
                      )}
                    </h3>
                  </div>

                  <ActionButtons student={student?.data} />
                </div>
              </div>
            </Card>
          )}

          {activeStep === 0 && (
            <PersonalData
              student={student}
              passportFile={passportFile}
              setPassportFile={setPassportFile}
              isCreatingStudent={!student?.data?.studentId}
            />
          )}
          {activeStep === 1 && <StudentData student={student} />}
          {activeStep === 2 && <AdditionalInformation student={student} />}
          {activeStep === 3 && <GradeData key={student?.data?.studentId} student={student} />}
          {activeStep === 4 && <PreviousEducation student={student} />}
          {activeStep === 5 && <PaymentsInformation student={student} />}
          {activeStep === 6 && <StudentDocuments student={student} />}
        </div>

        {studentNav}

        <Toast ref={toast} />
      </FormikProvider>
    </div>
  );
};

export default StudentProfile;
